import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthenticationStatus from './AuthenticationStatus';

const AccountSecurityOverview = ({ user, onProfileUpdate }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!user) return null;

  const isVerified = user.isEmailVerified;
  const plan = user.plan || 'free';
  const registrationDate = user.createdAt ? new Date(user.createdAt).toLocaleDateString('ja-JP') : '不明';
  const lastLoginDate = user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString('ja-JP') : '不明';

  // セキュリティスコアの計算
  const calculateSecurityScore = () => {
    let score = 0;
    let total = 0;
    
    // メール認証 (最重要)
    total += 40;
    if (isVerified) score += 40;
    
    // プランレベル
    total += 20;
    if (['premium', 'premium_plus', 'ultimate'].includes(plan)) score += 20;
    else if (plan === 'standard') score += 15;
    else if (plan === 'free') score += 10;
    
    // アカウント年数
    total += 20;
    if (user.createdAt) {
      const daysSinceRegistration = (Date.now() - new Date(user.createdAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceRegistration > 30) score += 20;
      else if (daysSinceRegistration > 7) score += 15;
      else score += 5;
    }
    
    // 最終ログイン（アクティブ度）
    total += 20;
    if (user.lastLoginDate) {
      const daysSinceLogin = (Date.now() - new Date(user.lastLoginDate)) / (1000 * 60 * 60 * 24);
      if (daysSinceLogin < 1) score += 20;
      else if (daysSinceLogin < 7) score += 15;
      else if (daysSinceLogin < 30) score += 10;
      else score += 5;
    }
    
    return Math.round((score / total) * 100);
  };

  const securityScore = calculateSecurityScore();
  
  const getSecurityLevel = (score) => {
    if (score >= 80) return { level: 'HIGH', color: 'green', text: '高', icon: '🔒' };
    if (score >= 60) return { level: 'MEDIUM', color: 'yellow', text: '中', icon: '🔓' };
    return { level: 'LOW', color: 'red', text: '低', icon: '⚠️' };
  };

  const securityLevel = getSecurityLevel(securityScore);

  return (
    <div className="space-y-4">
      {/* メイン認証状態 */}
      <AuthenticationStatus user={user} onResendVerification={onProfileUpdate} />
      
      {/* アカウントセキュリティ概要 */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900 flex items-center space-x-2">
            <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>アカウントセキュリティ</span>
          </h3>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {showDetails ? '簡単表示' : '詳細表示'}
          </button>
        </div>

        {/* セキュリティスコア表示 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg bg-${securityLevel.color}-500`}>
              {securityLevel.icon}
            </div>
            <div>
              <p className="font-medium text-gray-900">
                セキュリティレベル: <span className={`text-${securityLevel.color}-600`}>{securityLevel.text}</span>
              </p>
              <p className="text-sm text-gray-600">
                スコア: {securityScore}/100
              </p>
            </div>
          </div>
          
          {/* プログレスバー */}
          <div className="w-32">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className={`bg-${securityLevel.color}-500 h-2 rounded-full transition-all duration-300`}
                style={{ width: `${securityScore}%` }}
              />
            </div>
          </div>
        </div>

        {/* 詳細情報 */}
        {showDetails && (
          <div className="space-y-3 pt-3 border-t border-gray-100">
            <h4 className="font-medium text-gray-800 text-sm">アカウント詳細情報</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-600">メールアドレス</p>
                <p className="font-medium text-gray-900 truncate">{user.email}</p>
                <div className="mt-1">
                  {isVerified ? (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">認証済み</span>
                  ) : (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">未認証</span>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-600">プラン</p>
                <p className="font-medium text-gray-900 capitalize">{plan}</p>
                <div className="mt-1">
                  <span className={`px-2 py-1 rounded text-xs ${
                    plan === 'ultimate' ? 'bg-amber-100 text-amber-800' :
                    plan === 'premium_plus' ? 'bg-indigo-100 text-indigo-800' :
                    plan === 'premium' ? 'bg-purple-100 text-purple-800' :
                    plan === 'standard' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {plan === 'ultimate' ? '👑 アルティメット' :
                     plan === 'premium_plus' ? '💎 プレミアムプラス' :
                     plan === 'premium' ? '🌟 プレミアム' :
                     plan === 'standard' ? '📊 スタンダード' :
                     '🆓 フリー'}
                  </span>
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-600">登録日</p>
                <p className="font-medium text-gray-900">{registrationDate}</p>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-600">最終ログイン</p>
                <p className="font-medium text-gray-900">{lastLoginDate}</p>
              </div>
            </div>
            
            {/* セキュリティ改善提案 */}
            {securityScore < 80 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <h5 className="font-medium text-yellow-800 text-sm mb-2">📈 セキュリティ向上のための提案</h5>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {!isVerified && (
                    <li>• メールアドレスの認証を完了してください（+40点）</li>
                  )}
                  {plan === 'free' && (
                    <li>• より安全な有料プランへのアップグレードをご検討ください（+10点）</li>
                  )}
                  {user.lastLoginDate && (Date.now() - new Date(user.lastLoginDate)) / (1000 * 60 * 60 * 24) > 7 && (
                    <li>• 定期的なログインでアカウントをアクティブに保ってください（+5点）</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 危険な操作セクション */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-sm">
        <h3 className="font-medium text-red-900 flex items-center space-x-2 mb-3">
          <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>危険な操作</span>
        </h3>
        
        <p className="text-sm text-red-700 mb-4">
          以下の操作は元に戻すことができません。慎重に実行してください。
        </p>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-white border border-red-200 rounded-lg">
            <div>
              <h4 className="font-medium text-red-900 text-sm">アカウント削除</h4>
              <p className="text-xs text-red-700">
                すべてのデータが完全に削除され、復元できません
              </p>
            </div>
            <Link
              to="/delete-account"
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              退会手続きへ
            </Link>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            ⚠️ <strong>注意：</strong> アカウントを削除すると、すべての相談履歴・設定が完全に削除されます。
            退会理由をお聞かせいただけると、サービス改善に役立ちます。
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountSecurityOverview;